import { useLayoutContext } from "../../hooks/layout-context.js";
import { cn } from "../../utils/classnames.js";
import { getLayoutRegionNavigationDirectory } from "../../utils/navigation.js";
import { getTargetBlankA11yHint } from "../../utils/i18n.js";
import { useMemo } from "react";
import { prop, sortBy } from "remeda";
import { jsx, jsxs } from "react/jsx-runtime";
import { useLingui } from "@lingui/react";
// src/components/organisms/website-footer.tsx
const currentYear = (/* @__PURE__ */ new Date()).getFullYear();
const WebsiteFooter = ({ classNames }) => {
	const { i18n, _: $__ } = useLingui();
	const { Link, layoutConfig: { brand, project, navigation } } = useLayoutContext();
	/**
	* All navigation groups containing only the links relevant for this component.
	*/ const navDirectory = useMemo(() => getLayoutRegionNavigationDirectory("footer", navigation.directory), [navigation.directory]);
	const copyrightText = `© ${project?.copyrightSinceYear ? `${project?.copyrightSinceYear} — ` : ""}${currentYear} ${brand.name}. ${project?.copyrightAppendix ?? $__({
		id: "70Cb-2",
		message: "All rights reserved."
	})}`;
	const iconLinks = useMemo(() => navDirectory.iconLinks && sortBy(navDirectory.iconLinks.flatMap(prop("items")), (link) => "globalPriority" in link ? link.globalPriority : Number.POSITIVE_INFINITY).map(({ icon: Icon, ...link }) => {
		const a11yAwareProps = link.isExternal ? {
			href: link.href,
			a11yHint: getTargetBlankA11yHint(i18n)
		} : { href: link.href };
		return /* @__PURE__ */ jsx(Link, {
			title: link.hint,
			className: "text-muted-foreground hover:text-primary-text transition-colors",
			"aria-label": link.hint,
			...a11yAwareProps,
			children: /* @__PURE__ */ jsx(Icon, {
				className: "size-5",
				"aria-hidden": "true"
			})
		}, link.hint + link.href);
	}), [
		Link,
		i18n,
		navDirectory.iconLinks
	]);
	return /* @__PURE__ */ jsx("footer", {
		className: cn("border-muted dark:border-border border-t", classNames?.root),
		children: /* @__PURE__ */ jsxs("div", {
			className: "max-w-7xl px-4 md:px-6 lg:px-8 py-12 mx-auto",
			children: [/* @__PURE__ */ jsxs("div", {
				className: "gap-15 lg:gap-8 lg:flex-row flex flex-col",
				children: [/* @__PURE__ */ jsxs("div", {
					className: "max-w-md flex w-full flex-col",
					children: [
						/* @__PURE__ */ jsxs(Link, {
							href: navigation.rootRoutePathname,
							className: "space-x-3 mb-4 flex items-center",
							children: [/* @__PURE__ */ jsx("img", {
								alt: "",
								src: project?.logoImageSrc ?? brand.logoImageSrc,
								className: "size-8"
							}), /* @__PURE__ */ jsx("span", {
								className: "text-xl font-bold",
								children: project?.canonicalName ?? brand.name
							})]
						}),
						(project?.description ?? brand.tagline) && /* @__PURE__ */ jsx("p", {
							className: "text-muted-foreground mb-4 max-w-md",
							children: project?.description ?? brand.tagline
						}),
						/* @__PURE__ */ jsx("div", {
							className: "gap-4 flex",
							children: iconLinks
						})
					]
				}), /* @__PURE__ */ jsx("div", {
					className: "gap-20 lg:gap-8 lg:justify-around flex w-full flex-wrap justify-start",
					children: navDirectory.labeledLinks.map((group, idx) => /* @__PURE__ */ jsxs("div", {
						className: "gap-4 flex flex-col",
						children: [/* @__PURE__ */ jsx("h2", {
							className: "text-sm font-semibold tracking-wider uppercase",
							children: group.groupTitle
						}), /* @__PURE__ */ jsx("ul", {
							className: "gap-3 flex flex-col",
							children: sortBy(group.items, (link) => "globalPriority" in link ? link.globalPriority : Number.POSITIVE_INFINITY).map((link) => {
								const a11yAwareProps = link.isExternal ? {
									href: link.href,
									a11yHint: getTargetBlankA11yHint(i18n)
								} : { href: link.href };
								return /* @__PURE__ */ jsx("li", { children: /* @__PURE__ */ jsx(Link, {
									className: "text-muted-foreground hover:text-primary-text transition-colors",
									"aria-label": link.hint ?? link.label,
									...a11yAwareProps,
									children: link.label
								}) }, link.label + link.href);
							})
						})]
					}, idx + group.groupTitle))
				})]
			}), /* @__PURE__ */ jsx("div", {
				className: "mt-8 pt-8 border-border border-t",
				children: /* @__PURE__ */ jsx("p", {
					className: "text-muted-foreground text-sm text-center",
					children: copyrightText
				})
			})]
		})
	});
};
export { WebsiteFooter };

//# sourceMappingURL=website-footer.js.map